import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, MoveUp, MoveDown, ExternalLink, Copy, Check } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { profilePagesAPI, linksAPI } from '../services/api';
import Button from './common/Button';
import Card from './common/Card';
import Input from './common/Input';
import Textarea from './common/Textarea';
import Alert from './common/Alert';
import LoadingSpinner from './common/LoadingSpinner';
import { SHORT_LINK_BASE_URL } from '../config';

const CreateProfilePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    name: '',
    title: '',
    description: '',
    theme: 'default',
    is_active: true,
    avatarUrl: '',
    bio: '',
    themeColor: '',
    socialLinks: [],
    leadCaptureEnabled: false,
    leadCapturePrompt: ''
  });

  const [selectedLinks, setSelectedLinks] = useState([]);
  const [availableLinks, setAvailableLinks] = useState([]);
  const [showLinkSelector, setShowLinkSelector] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [copiedPageUrl, setCopiedPageUrl] = useState(false);

  // Fetch profile page data if editing
  const { loading: pageLoading, error: pageError, executeApiCall: executeFetchPage } = useApi();
  const [pageData, setPageData] = useState(null);

  // Fetch available links
  const { executeApiCall: executeFetchLinks } = useApi();
  const [linksData, setLinksData] = useState(null);

  const fetchPage = useCallback(async () => {
    if (id) {
      try {
        const result = await executeFetchPage(profilePagesAPI.getById, id);
        if (result.success) {
          setPageData(result.data.profilePage);
        } else {
          console.error('Failed to fetch profile page:', result.error);
        }
      } catch (err) {
        console.error('Error fetching profile page:', err);
      }
    }
  }, [executeFetchPage, id]);

  const fetchLinks = useCallback(async () => {
    const result = await executeFetchLinks(linksAPI.getAll);
    if (result.success) {
      setLinksData(result.data);
    }
  }, [executeFetchLinks]);

  useEffect(() => {
    if (isEditing) {
      fetchPage();
    }
    fetchLinks();
  }, [isEditing, fetchPage, fetchLinks]);

  useEffect(() => {
    if (pageData) {
      const page = pageData;
      setFormData({
        name: page.name,
        title: page.title,
        description: page.description || '',
        theme: page.theme,
        is_active: page.is_active,
        avatarUrl: page.avatar_url || '',
        bio: page.bio || '',
        themeColor: page.theme_color || '',
        socialLinks: Array.isArray(page.social_links) ? page.social_links : [],
        leadCaptureEnabled: !!page.lead_capture_enabled,
        leadCapturePrompt: page.lead_capture_prompt || ''
      });
      setSelectedLinks(page.links || []);
    }
  }, [pageData]);

  useEffect(() => {
    if (linksData) {
      setAvailableLinks(linksData.links || []);
    }
  }, [linksData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addSocial = () =>
    setFormData(prev => ({ ...prev, socialLinks: [...prev.socialLinks, { platform: '', url: '' }] }));
  const updateSocial = (i, field, value) =>
    setFormData(prev => ({
      ...prev,
      socialLinks: prev.socialLinks.map((s, idx) => (idx === i ? { ...s, [field]: value } : s))
    }));
  const removeSocial = (i) =>
    setFormData(prev => ({ ...prev, socialLinks: prev.socialLinks.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let response;
      if (isEditing) {
        response = await profilePagesAPI.update(id, formData);
      } else {
        response = await profilePagesAPI.create(formData);
      }

      if (response.success) {
        setSuccess(isEditing ? 'Profile page updated successfully!' : 'Profile page created successfully!');
        
        // If creating, navigate to the edit page to add links
        if (!isEditing && response.data && response.data.profilePage) {
          setTimeout(() => {
            navigate(`/profile-pages/${response.data.profilePage.id}/edit`);
          }, 1500);
        }
      } else {
        setError(response.error || 'An error occurred');
      }
    } catch (err) {
      setError('An error occurred while saving the profile page');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLink = async (linkId) => {
    try {
      const response = await profilePagesAPI.addLink(id, { linkId });
      if (response.success) {
        // Refresh the page data to get updated links
        fetchPage();
        setShowLinkSelector(false);
      } else {
        setError(response.error || 'Failed to add link');
      }
    } catch (err) {
      setError('Failed to add link to page');
    }
  };

  const handleRemoveLink = async (linkId) => {
    if (!window.confirm('Are you sure you want to remove this link from the page?')) {
      return;
    }

    try {
      const response = await profilePagesAPI.removeLink(id, linkId);
      if (response.success) {
        // Refresh the page data to get updated links
        fetchPage();
      } else {
        setError(response.error || 'Failed to remove link');
      }
    } catch (err) {
      setError('Failed to remove link from page');
    }
  };

  const handleMoveLink = async (linkId, direction) => {
    const currentIndex = selectedLinks.findIndex(link => link.link_id === linkId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= selectedLinks.length) return;

    try {
      const response = await profilePagesAPI.updateLinkOrder(id, linkId, { displayOrder: newIndex + 1 });
      if (response.success) {
        // Refresh the page data to get updated links
        fetchPage();
      } else {
        setError(response.error || 'Failed to update link order');
      }
    } catch (err) {
      setError('Failed to update link order');
    }
  };

  const getPageUrl = () => {
    if (!pageData) return '';
    const username = pageData.username;
    const pageName = formData.name;
    if (!username || !pageName) return '';
    return `${SHORT_LINK_BASE_URL}/p/${username}/${pageName}`;
  };

  const handleCopyPageUrl = async () => {
    try {
      const url = getPageUrl();
      if (!url) return;
      await navigator.clipboard.writeText(url);
      setCopiedPageUrl(true);
      setTimeout(() => setCopiedPageUrl(false), 2000);
    } catch (e) {
      // no-op
    }
  };

  const getAvailableLinksForSelection = () => {
    const selectedLinkIds = selectedLinks.map(link => link.link_id);
    return availableLinks.filter(link => !selectedLinkIds.includes(link.id));
  };

  if (isEditing && pageLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (isEditing && pageError) {
    return (
      <Alert
        type="error"
        title="Error"
        message="Failed to load profile page data. Please try again."
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <button
          onClick={() => navigate('/profile-pages')}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-500 hover:text-ink-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to profile pages
        </button>
        <h1 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">
          {isEditing ? 'Edit profile page' : 'Create profile page'}
        </h1>
        <p className="mt-1.5 text-sm text-ink-500">
          {isEditing
            ? 'Update your profile page settings and links.'
            : 'A link-in-bio style page that bundles all your links in one place.'}
        </p>
      </div>

      {error && (
        <Alert
          type="error"
          title="Error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {success && (
        <Alert
          type="success"
          title="Success"
          message={success}
          onClose={() => setSuccess(null)}
        />
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {isEditing && pageData && (
          <Card>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-ink-900">Page URL</h3>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={handleCopyPageUrl}
                    className="text-ink-600 hover:text-ink-800"
                    title="Copy Page URL"
                  >
                    {copiedPageUrl ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </button>
                  <a
                    href={getPageUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700"
                    title="Open Page"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
              <div className="p-3 bg-ink-50 rounded border break-all text-sm text-ink-700">
                {getPageUrl() || 'Page URL will appear here'}
              </div>
            </div>
          </Card>
        )}
        <Card>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Page Name *
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="my-profile-page"
                  required
                  disabled={isEditing} // Name cannot be changed after creation
                />
                <p className="text-sm text-ink-500 mt-1">
                  {isEditing 
                    ? 'Page name cannot be changed after creation'
                    : 'This will be part of your page URL (letters, numbers, spaces, and hyphens only)'
                  }
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Theme
                </label>
                <select
                  value={formData.theme}
                  onChange={(e) => handleInputChange('theme', e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm text-ink-900 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
                >
                  <option value="default">Default</option>
                  <option value="dark">Dark</option>
                  <option value="light">Light</option>
                  <option value="sunset">Sunset</option>
                  <option value="ocean">Ocean</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-700 mb-2">
                Page Title *
              </label>
              <Input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="My Awesome Profile"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-700 mb-2">
                Description
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Tell visitors about yourself or your page..."
                rows={3}
              />
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  className="rounded border-ink-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-ink-700">Active (visible to visitors)</span>
              </label>
            </div>
          </div>
        </Card>

        {/* Branding, socials & lead capture */}
        <Card>
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-ink-900">Branding & bio</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">Avatar image URL</label>
                <Input
                  type="url"
                  value={formData.avatarUrl}
                  onChange={(e) => handleInputChange('avatarUrl', e.target.value)}
                  placeholder="https://example.com/me.jpg"
                />
                <p className="text-sm text-ink-500 mt-1">Shown as a circular avatar and used as the social preview image.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">Accent color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={formData.themeColor || '#6366f1'}
                    onChange={(e) => handleInputChange('themeColor', e.target.value)}
                    className="h-10 w-14 rounded border border-ink-200 bg-white p-1"
                  />
                  <Input
                    type="text"
                    value={formData.themeColor}
                    onChange={(e) => handleInputChange('themeColor', e.target.value)}
                    placeholder="#6366f1"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-700 mb-2">Bio</label>
              <Textarea
                value={formData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                placeholder="A short bio shown under your name…"
                rows={2}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-ink-700">Social links</label>
                <Button type="button" size="small" variant="secondary" onClick={addSocial}>
                  <Plus className="h-4 w-4" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {formData.socialLinks.length === 0 && (
                  <p className="text-sm text-ink-500">No social links yet.</p>
                )}
                {formData.socialLinks.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={s.platform}
                      onChange={(e) => updateSocial(i, 'platform', e.target.value)}
                      placeholder="Twitter"
                    />
                    <Input
                      type="url"
                      value={s.url}
                      onChange={(e) => updateSocial(i, 'url', e.target.value)}
                      placeholder="https://twitter.com/you"
                    />
                    <button
                      type="button"
                      onClick={() => removeSocial(i)}
                      className="text-red-600 hover:text-red-700 shrink-0"
                      aria-label="Remove social link"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-ink-100 pt-5 space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.leadCaptureEnabled}
                  onChange={(e) => handleInputChange('leadCaptureEnabled', e.target.checked)}
                  className="rounded border-ink-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-ink-700">Enable email capture form</span>
              </label>
              {formData.leadCaptureEnabled && (
                <Input
                  type="text"
                  value={formData.leadCapturePrompt}
                  onChange={(e) => handleInputChange('leadCapturePrompt', e.target.value)}
                  placeholder="Join my mailing list"
                />
              )}
            </div>
          </div>
        </Card>

        {isEditing && (
          <Card>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-ink-900">Page Links</h3>
                <Button
                  type="button"
                  onClick={() => setShowLinkSelector(!showLinkSelector)}
                  disabled={getAvailableLinksForSelection().length === 0}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Link
                </Button>
              </div>

              {showLinkSelector && (
                <div className="border border-ink-200 rounded-lg p-4 bg-ink-50">
                  <h4 className="text-sm font-medium text-ink-700 mb-3">Select a link to add:</h4>
                  <div className="space-y-2">
                    {getAvailableLinksForSelection().map(link => (
                      <div
                        key={link.id}
                        className="flex items-center justify-between p-3 bg-white rounded border hover:bg-ink-50"
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium text-ink-900">
                            {link.title || `${SHORT_LINK_BASE_URL}/r/${link.short_code}`}
                          </div>
                          <div className="text-sm text-ink-500">
                            {link.title ? `${SHORT_LINK_BASE_URL}/r/${link.short_code}` : link.original_url}
                          </div>
                        </div>
                        <Button
                          type="button"
                          onClick={() => handleAddLink(link.id)}
                          size="sm"
                        >
                          Add
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedLinks.length === 0 ? (
                <div className="text-center py-8 text-ink-500">
                  <p>No links added yet. Click "Add Link" to get started.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedLinks.map((link, index) => (
                    <div
                      key={link.id}
                      className="flex items-center justify-between p-3 bg-ink-50 rounded border"
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-ink-900">
                          {link.title || `${SHORT_LINK_BASE_URL}/r/${link.short_code}`}
                        </div>
                        <div className="text-sm text-ink-500">
                          {link.title ? `${SHORT_LINK_BASE_URL}/r/${link.short_code}` : link.original_url}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => handleMoveLink(link.link_id, 'up')}
                          disabled={index === 0}
                          className="text-ink-400 hover:text-ink-600 disabled:opacity-50"
                        >
                          <MoveUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveLink(link.link_id, 'down')}
                          disabled={index === selectedLinks.length - 1}
                          className="text-ink-400 hover:text-ink-600 disabled:opacity-50"
                        >
                          <MoveDown className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveLink(link.link_id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        )}

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/profile-pages')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {isEditing ? 'Update Page' : 'Create Page'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateProfilePage;
